/**
 * 01 · Az esemény. Handover §5.2, §6.1–§6.3, §8.1, and `build/FIXES.md`
 * item 2 for the savoury/sweet balance that took the *Mennyiség* card's
 * place.
 *
 * There is no quantity control on this page and none anywhere else: the
 * *Mennyiség* card and the *Kevesebb · Javasolt · Több* levels are retired
 * (Barna, 4 October). Appetite is the guest profile below, and the balance
 * row sits directly after it.
 */

import {
  BALANCE_CHOICES,
  GUEST_PROFILES,
  OCCASION_HINTS,
  SERVING_WINDOWS,
} from '../catalogue.ts'
import { Choices, Note } from '../components.tsx'
import {
  CONFERENCE,
  CONFERENCE_MAX_BREAKS,
  CONFERENCE_MIN_BREAKS,
  LIGHT_DINNER,
  OTHER_OCCASION,
  toNumber,
} from '@plt/engine'
import type { PageBundle } from '../bundle.ts'
import {
  MEAL_DEFAULT_FROM_PARAM,
  RECEPTION_CHIP,
  isReception,
  mealReplacementDefault,
  occasionForMeal,
  outsideDeliveryRadius,
  outsideRadiusSentence,
  replacesMeal,
  slotWindow,
} from '../model.ts'
import { guestsOf } from '../state.ts'
import { withPostcode, type TownTable } from '../towns.ts'
/* `build/FIXES.md` item 79 (F8): one component for the confirmation note, so
   the note F1 shows and the note above the order button are the same four
   sentences with the same two parameter times in them. */
import { ConfirmationNote } from './Step4Summary.tsx'
/* Run 19 §4: the meal note has one source on the page, and this is where the
   question that sets it also reads it from. */
import { PickupCallout, mealNote } from './Notices.tsx'

export function Step1Event({ page }: { page: PageBundle }): JSX.Element {
  const { t, f, state, set, occasion } = page
  const guests = guestsOf(state)

  return (
    <section id="sec1">
      <div className="sechead">
        <span className="n">01</span>
        <h2>{t('Az esemény', 'The event')}</h2>
      </div>

      <div className="card">
        <label className="f" htmlFor="evname">
          {t('Az esemény neve', 'Event name')}
        </label>
        <input
          id="evname"
          type="text"
          value={state.eventName}
          placeholder={t('pl. Vezetői workshop, 2026 ősz', 'e.g. Leadership workshop, autumn 2026')}
          onChange={(event) => set({ eventName: event.target.value })}
        />

        <div className="grid" style={{ marginTop: 12 }}>
          <div>
            <label className="f" htmlFor="covers">
              {t('Vendégek száma', 'Number of guests')}
            </label>
            <input
              type="number"
              id="covers"
              min={1}
              max={400}
              inputMode="numeric"
              placeholder="0"
              value={state.guestsField}
              onChange={(event) => {
                /* §5.2: 1 to 400. The field is corrected on screen so a typed
                   500 does not sit there while every number on the page is
                   for 400; an empty field is left alone, because it shows no
                   number and so contradicts none. DR-7 (FIXES item 10): the
                   line that explains the correction stays until the customer
                   types a figure inside the range. */
                const raw = event.target.value
                if (raw !== '' && Number(raw) > 400) {
                  set({ guestsField: '400', guestsCapped: true })
                  return
                }
                if (raw !== '' && Number(raw) < 1) {
                  set({ guestsField: '1', guestsCapped: false })
                  return
                }
                set({ guestsField: raw, guestsCapped: false })
              }}
            />
          </div>
          <div>
            <label className="f" htmlFor="date">
              {t('Dátum', 'Date')}
            </label>
            <div className="datef">
              <input
                type="text"
                id="dateTxt"
                readOnly
                tabIndex={-1}
                value={page.calendar.eventDate === null ? '' : f.date(page.calendar.eventDate, true)}
              />
              <input
                type="date"
                id="date"
                aria-label={t('Dátum', 'Date')}
                value={state.date}
                /* Fix list item 3 (10 October 2026): the field is invisible
                   over the formatted date, so on desktop a click landed on an
                   unseen segment and no calendar opened — the date looked
                   unchangeable. A click or Enter/Space opens the picker. */
                onClick={(event) => openPicker(event.currentTarget)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    openPicker(event.currentTarget)
                  }
                }}
                onChange={(event) => set({ date: event.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="f" htmlFor="start">
              {t('Kezdés', 'Start time')}
            </label>
            <select
              id="start"
              value={state.start}
              onChange={(event) => set({ start: event.target.value })}
            >
              {START_TIMES.map((time) => (
                <option key={time}>{time}</option>
              ))}
            </select>
          </div>
        </div>

        {/* DR-7 (FIXES item 10): the headcount was corrected to the figure the
            page is using, and one quiet line in the same card says why. */}
        <div id="coversNote">
          {state.guestsCapped ? (
            <Note>
              {t(
                'Legfeljebb 400 vendégre tudunk itt rendelést felvenni.',
                'We can take orders here for up to 400 guests.',
              )}
            </Note>
          ) : null}
        </div>

        <div className="fld">
          <label className="f">{t('Milyen alkalomra kéri?', 'What is the occasion?')}</label>
          <Choices
            id="occ"
            dataAttribute="occ"
            choices={occasionChoices(page)}
            value={isReception(state.occasion) ? RECEPTION_CHIP : state.occasion}
            onChoose={(value) =>
              /* A different occasion earns a different recommendation, so the
                 menu is built again for it (DR-21, FIXES item 24, which took
                 the headcount band out of this key and left the occasion in).
                 `build/FIXES.md` item 78 (F7): *Switching to another occasion
                 clears the coffee answer* — including between the two
                 occasions that ask it, because the day they stand for is a
                 different day and the line's quantity with it.

                 Run 17 item B9(a): the one *Állófogadás* chip stands for two
                 register rows. Choosing it picks the row item B11(a)'s
                 pre-selection names for the start time in force, and the
                 customer can change it with the question underneath. */
              set({
                occasion:
                  value === RECEPTION_CHIP
                    ? occasionForMeal(
                        mealReplacementDefault(state.start, page.param(MEAL_DEFAULT_FROM_PARAM)),
                      )
                    : value,
                coffee: false,
                mealTouched: false,
              })
            }
          />
          <div className="sub" id="occHint" style={{ marginTop: 6 }}>
            {t.of(OCCASION_HINTS[state.occasion] ?? null)}
          </div>
          {/* Run 17 item B9(b): what a boxed Munkaebéd is, said where it is
              chosen. */}
          {state.occasion === 'munka' ? (
            <div className="sub" id="munkaBoxNote" style={{ marginTop: 6 }}>
              {t(
                'Az ebédet vendégenként külön dobozba csomagoljuk.',
                'Lunch is packed in an individual box for each guest.',
              )}
            </div>
          ) : null}
          {/* Run 19 §5 (DECIDED 20261016, Barna): *Állófogadás* is a buffet,
              and §18 ties how the food leaves the kitchen to the staff
              answer — larger shared boxes without on-site service, trays
              under cling film with it. Both are named, in the same place and
              style as the *Munkaebéd* note above. */}
          {isReception(state.occasion) ? (
            <div className="sub" id="fogadasBuffetNote" style={{ marginTop: 6 }}>
              {t(
                'Svédasztalos tálaláshoz. Nagyobb, közös dobozokban érkezik, helyszíni kiszolgálással fóliázott tálcákon.',
                'For a buffet. Arrives in larger shared boxes, or on cling-filmed trays with on-site service.',
              )}
            </div>
          ) : null}
          {/* Run 19 §6: *Könnyű vacsora* is *Állófogadás* answered *Igen*,
              so its note says the buffet, the meal and the packing at once —
              the meal question it replaces is not asked. */}
          {state.occasion === LIGHT_DINNER ? (
            <div className="sub" id="lightDinnerNote" style={{ marginTop: 6 }}>
              {t(
                'Svédasztalos tálaláshoz, vacsorának is elegendő. Nagyobb, közös dobozokban érkezik, helyszíni kiszolgálással fóliázott tálcákon.',
                'For a buffet, and enough for dinner. Arrives in larger shared boxes, or on cling-filmed trays with on-site service.',
              )}
            </div>
          ) : null}
        </div>

        <MealQuestion page={page} />
        <Conference page={page} />
        <Coffee page={page} />

        {occasion.servingWindowApplies ? (
          <div className="fld" id="durWrap">
            <label className="f">
              {t('Mennyi ideig lesz kint az étel?', 'How long will the food be out?')}
            </label>
            <Choices
              id="dur"
              dataAttribute="dur"
              choices={SERVING_WINDOWS.map((window) => ({
                value: window.key,
                label: t.of(window.label),
              }))}
              value={state.servingWindow}
              onChoose={(value) => set({ servingWindow: value })}
            />
            <div className="sub" id="durHint" style={{ marginTop: 6 }}>
              {t(
                'Nem az esemény hossza számít, hanem az, hogy meddig kínálják az ételt.',
                'What counts is how long the food is served, not how long the event runs.',
              )}
            </div>
          </div>
        ) : null}

        <div className="fld">
          <label className="f">
            {t('Milyen étkű a közönség?', 'How much do your guests eat?')}
          </label>
          <Choices
            id="prof"
            dataAttribute="prof"
            choices={GUEST_PROFILES.map((profile) => ({
              value: profile.key,
              label: t.of(profile.label),
              example: profile.example === null ? null : t.of(profile.example),
            }))}
            value={state.profile}
            onChoose={(value) => set({ profile: value })}
          />
        </div>

        {/* The savoury/sweet balance (build/FIXES.md item 2): three discrete
            choices, no slider, directly after the appetite profile. The line
            under it is the same figure the sticky summary shows (§5.4) — every
            guest counted, gluten-free portions included — to one decimal, so
            each choice visibly moves it. */}
        <div className="fld">
          <label className="f">{t('Sós és édes aránya', 'Savoury and sweet')}</label>
          <Choices
            id="bal"
            dataAttribute="bal"
            choices={BALANCE_CHOICES.map((choice) => ({
              value: choice.key,
              label: t.of(choice.label),
            }))}
            value={state.balance}
            onChoose={(value) => set({ balance: value })}
          />
          <div className="sub" id="balHint" style={{ marginTop: 6 }}>
            <BalanceHint page={page} />
          </div>
        </div>

        {/* `build/FIXES.md` item 72 (F1), Barna's ruling 1 of 8 October 2026:
            *The Planteen provides no equipment without staff.* One question,
            the brief's own words, no hint under it, and the three tick boxes
            — felszolgálás, tálak és tálalóeszközök, egyéb eszköz — gone with
            their state. *Igen* raises the `staff_equipment` confirmation
            reason (`rulesOf`); the note below is today's enquiry note, which
            `083_confirm_first` replaces with F8's confirmation note. */}
        <div className="fld">
          <label className="f">
            {t(
              'Kér helyszíni kiszolgálást személyzettel és eszközökkel?',
              'Do you need on-site service with staff and equipment?',
            )}
          </label>
          <Choices
            id="staff"
            dataAttribute="staff"
            choices={[
              { value: 'false', label: t('Nem', 'No') },
              { value: 'true', label: t('Igen', 'Yes') },
            ]}
            value={String(state.staff)}
            onChoose={(value) => set({ staff: value === 'true' })}
          />
          {state.staff ? (
            /* `build/FIXES.md` item 79 (F8): the confirmation note "replaces
               the former enquiry note (*Ezt személyesen egyeztetjük…*)
               everywhere except on the short-notice enquiry, including the
               notes F1, F4 and F7 show". This is F1's. The old sentence is
               not reworded, it is gone: it promised a telephone call and an
               unpriced enquiry, and neither happens any more. */
            <div id="staffNote">
              <ConfirmationNote page={page} />
            </div>
          ) : null}
        </div>

        {/* Run 9, item 2 (DECIDED 20261010): *A Planteen lesz az egyetlen étel
            a rendezvényen?* is gone -- "assume we feed everyone". The state
            keeps `planteenOnly` at its default, true, and nothing reads it. */}
      </div>

      <div className="card">
        <h3>{t('Étrendi igények', 'Dietary needs')}</h3>
        <div className="grid" style={{ marginTop: 12 }}>
          <div>
            <label className="f" htmlFor="d1">
              {t('Gluténmentesen étkező fő', 'Gluten-free guests')}
            </label>
            <input
              type="number"
              id="d1"
              min={0}
              max={guests}
              inputMode="numeric"
              value={String(Math.min(Math.max(state.glutenFreeGuests, 0), guests))}
              onChange={(event) =>
                /* DR-7, option i: the state keeps the figure the customer
                   typed, so the correction can be shown. Clamping it here
                   defeated that in vB6 — every reader clamps for itself. */
                set({ glutenFreeGuests: Math.max(0, Math.round(Number(event.target.value) || 0)) })
              }
            />
          </div>
          <div style={{ gridColumn: '1/-1' }}>
            <label className="f" htmlFor="dnote">
              {t('Egyéb allergia vagy étrendi igény', 'Any other allergy or dietary need')}
            </label>
            <input
              type="text"
              id="dnote"
              maxLength={240}
              aria-describedby="dnoteHint"
              value={state.dietaryNote}
              placeholder={t(
                'pl. két vendég diófélékre érzékeny',
                'e.g. two guests are allergic to tree nuts',
              )}
              onChange={(event) => set({ dietaryNote: event.target.value })}
            />
            <p className="sub" id="dnoteHint" style={{ marginTop: 5 }}>
              {t(
                'Kérjük, ne írjon ide nevet vagy más személyes adatot, csak az igényt.',
                'Please do not include names or other personal details here, only the need.',
              )}
            </p>
          </div>
        </div>

        {/* DR-7: the same, for the gluten-free count against the headcount. */}
        <div id="d1Note">
          {state.glutenFreeGuests > guests ? (
            <Note>
              {t(
                'A gluténmentes vendégek száma nem lehet több a vendégek számánál.',
                'Gluten-free guests cannot outnumber the guests.',
              )}
            </Note>
          ) : null}
        </div>

        <div id="gfNote">
          {state.glutenFreeGuests > 0 ? (
            <Note>
              <b>{t('Fontos', 'Important')}:</b>{' '}
              {t(
                'a gluténmentes vendégek a vendégszámon belül vannak, egyszer számoljuk őket: a teljes mennyiség nem nő. Az ő adagjaikat mi állítjuk össze gluténtartalmú összetevő nélkül készült fogásokból, külön dobozban és külön címkével. Konyhánk nem rendelkezik gluténmentes minősítéssel, ezért cöliákiás vendégek számára nem tudjuk garantálni a keresztszennyeződés-mentességet.',
                'gluten-free guests are part of the guest count and counted once: the total does not grow. We put their portions together from dishes made without gluten-containing ingredients, in separate, labelled boxes. Our kitchen is not certified gluten-free, so we cannot guarantee freedom from cross-contamination for guests with coeliac disease.',
              )}
            </Note>
          ) : null}
        </div>
      </div>

      <Fulfilment page={page} />
      <Lane page={page} />
    </section>
  )
}

/**
 * **Run 17 item B9(a) (DECIDED 20261015, Barna).** The occasion chips, with
 * the two reception rows shown as one — *Állófogadás* / *Standing
 * reception*. Both `public.occasions` rows stay exactly as they are; only
 * the page's list is shorter. Any other occasion the register names keeps
 * its own label and its place.
 */
function occasionChoices(page: PageBundle): { value: string; label: string }[] {
  const { t, context } = page
  const out: { value: string; label: string }[] = []
  let receptionAdded = false
  for (const row of orderedOccasions(context.occasions)) {
    if (isReception(row.key)) {
      if (receptionAdded) continue
      receptionAdded = true
      out.push({ value: RECEPTION_CHIP, label: t('Állófogadás', 'Standing reception') })
      continue
    }
    out.push({
      value: row.key,
      label: t(row.name_hu ?? row.key, row.name_en ?? row.name_hu ?? row.key),
    })
  }
  return out
}

/**
 * **Run 17 item B11(a) (DECIDED 20261015, Barna): the meal question.**
 *
 *   *Kiváltja az ebédet vagy vacsorát?* / *Will this replace lunch or dinner?*
 *   *Igen* / *Yes* · *Nem, csak könnyű falatok* / *No, light bites only*
 *
 * On *Állófogadás* only. *Igen* uses the `fog_est` figure set and *Nem* the
 * `fog_nap` one; the duration and appetite multipliers apply as they do
 * today, because the answer picks a row and changes nothing else.
 *
 * **Pre-selected by the start time**, from `meal_default_from` (18:00): from
 * it *Igen*, before it *Nem*. The customer can change it, and once they have
 * — `state.mealTouched` — a later change of start time leaves their answer
 * alone. The live check of item B4 follows, because it reads the occasion.
 */
function MealQuestion({ page }: { page: PageBundle }): JSX.Element | null {
  const { t, state, set, param } = page
  if (!isReception(state.occasion)) return null
  return (
    <div className="fld" id="mealWrap">
      <label className="f">
        {t('Kiváltja az ebédet vagy vacsorát?', 'Will this replace lunch or dinner?')}
      </label>
      <Choices
        id="meal"
        dataAttribute="meal"
        choices={[
          { value: 'true', label: t('Igen', 'Yes') },
          { value: 'false', label: t('Nem, csak könnyű falatok', 'No, light bites only') },
        ]}
        value={String(replacesMeal(state.occasion))}
        onChoose={(value) =>
          set({ occasion: occasionForMeal(value === 'true'), mealTouched: true })
        }
      />
      {/* Run 19 §4: **both** answers say what they mean, and the words come
          from `mealNote` in Notices.tsx — the one source the summary, the
          offer card and (through `public.meal_replacement_note`) every
          document read. Run 18 item R18-2 wrote only the *Nem* half here. */}
      {mealNote(page) === null ? null : (
        <div className="sub" id="mealNote" style={{ marginTop: 6 }}>
          {mealNote(page)}
        </div>
      )}
      <div className="sub" id="mealDefaultNote" style={{ marginTop: 6 }} data-mealfrom={param(MEAL_DEFAULT_FROM_PARAM)} />
    </div>
  )
}

/**
 * `build/FIXES.md` item 73 (F2), Barna's ruling 2 of 8 October 2026: one
 * conference occasion made of an optional welcome, one to three coffee
 * breaks and an optional lunch. The engine half is `080_params_slots`'s —
 * `conferenceOccasionFrom` builds the per-head pieces out of the Kávészünet
 * and Munkaebéd rows at run time — and these are the three controls that
 * answer it. They open on `CONFERENCE_DEFAULTS`, F2's PROPOSED day, which
 * lives in the engine so the page and the server cannot drift apart.
 *
 * Labels and hints are the brief's own words and are final.
 */
function Conference({ page }: { page: PageBundle }): JSX.Element | null {
  const { t, state, set } = page
  if (state.occasion !== CONFERENCE) return null
  const breaks = Array.from(
    { length: CONFERENCE_MAX_BREAKS - CONFERENCE_MIN_BREAKS + 1 },
    (_, index) => String(CONFERENCE_MIN_BREAKS + index),
  )
  return (
    <div id="confWrap">
      <div className="fld">
        <label className="f">{t('Érkezéskor (welcome)', 'On arrival (welcome)')}</label>
        <Choices
          id="confWelcome"
          dataAttribute="cw"
          choices={[
            { value: 'false', label: t('Nem', 'No') },
            { value: 'true', label: t('Igen', 'Yes') },
          ]}
          value={String(state.conference.welcome)}
          onChoose={(value) =>
            set((current) => ({
              conference: { ...current.conference, welcome: value === 'true' },
            }))
          }
        />
      </div>

      <div className="fld">
        <label className="f">{t('Kávészünetek száma', 'Number of coffee breaks')}</label>
        <Choices
          id="confBreaks"
          dataAttribute="cb"
          choices={breaks.map((value) => ({ value, label: value }))}
          value={String(state.conference.breaks)}
          onChoose={(value) =>
            set((current) => ({
              conference: { ...current.conference, breaks: Number(value) },
            }))
          }
        />
      </div>

      <div className="fld">
        {/* Run 17 item B11(b): the optional lunch element is asked with the
            same wording as the reception's own meal question, and keeps its
            current default (*Igen*). *Nem* leaves the welcome and the break
            elements only, and no figure moves. */}
        <label className="f">{t('Kiváltja az ebédet?', 'Will this replace lunch?')}</label>
        <Choices
          id="confLunch"
          dataAttribute="cl"
          choices={[
            { value: 'false', label: t('Nem', 'No') },
            { value: 'true', label: t('Igen', 'Yes') },
          ]}
          value={String(state.conference.lunch)}
          onChoose={(value) =>
            set((current) => ({
              conference: { ...current.conference, lunch: value === 'true' },
            }))
          }
        />
        <div className="sub" id="confLunchHint" style={{ marginTop: 6 }}>
          {state.conference.lunch
            ? null
            : t('Az ebédet más biztosítja.', 'Lunch comes from elsewhere.')}
        </div>
      </div>
    </div>
  )
}

/**
 * `build/FIXES.md` item 78 (F7), ruling 3, as **run 19 §16** puts it live:
 * *Kér kávét is?*, beside the occasion controls, for Kávészünet and the
 * conference occasion only — **and only when the customer has asked for
 * on-site service with staff and equipment**, because the coffee needs the
 * machine and the machine needs somebody to run it.
 *
 * It was hidden on every occasion from run 7 to run 18, waiting on a
 * catalogue row that was never named. §16 settles that it is not a
 * catalogue row at all: a cup is priced from the register, and with
 * Balázs's figures of 16 October and Barna's two markups of 18 October the
 * question has an offer behind it. `coffeeOffer` in `model.ts` is still the
 * one place that decides whether it may be asked; `page.coffee` is its
 * answer, and a register missing any one of §16's eleven figures hides the
 * question and both offer lines together.
 *
 * The answer does **not** change the staff and equipment question of F1.
 * Each adds its own confirmation reason.
 */
function Coffee({ page }: { page: PageBundle }): JSX.Element | null {
  const { t, state, set, coffee } = page
  if (coffee === null) return null
  return (
    <div className="fld" id="coffeeWrap">
      <label className="f">{t('Kér kávét is?', 'Would you like coffee too?')}</label>
      <Choices
        id="coffee"
        dataAttribute="cof"
        choices={[
          { value: 'false', label: t('Nem', 'No') },
          { value: 'true', label: t('Igen', 'Yes') },
        ]}
        value={String(state.coffee)}
        onChoose={(value) => set({ coffee: value === 'true' })}
      />
      {state.coffee ? (
        <div className="sub" id="coffeeNote" style={{ marginTop: 6 }}>
          {/* Run 19 §16: the price is on the offer card now, so the note
              no longer promises it at confirmation. Coffee is still a
              confirmation reason — a colleague checks the day before the
              price is final — which the note above the order button says. */}
          {t(
            'A kávét személyzettel és eszközökkel, helyszíni kiszolgálással biztosítjuk. Az ajánlatban külön soron szerepel a kávé és a kávégép.',
            'We serve coffee on site, with staff and equipment. The offer shows the coffee and the machine on their own lines.',
          )}
        </div>
      ) : null}
    </div>
  )
}

/**
 * The line under the balance row: the same figure the sticky summary shows,
 * to one decimal. It is what is actually made divided by the whole guest
 * count, gluten-free portions included (§5.4), so every choice visibly moves
 * it.
 */
function BalanceHint({ page }: { page: PageBundle }): JSX.Element {
  const { t, f, priced } = page
  const savoury = f.n(toNumber(priced.priced.perGuest.savoury), 1)
  const sweet = f.n(toNumber(priced.priced.perGuest.sweet), 1)
  return t.lang === 'hu' ? (
    <>
      Vendégenként kb. <b className="num">{savoury}</b> sós és <b className="num">{sweet}</b> édes
      falat.
    </>
  ) : (
    <>
      About <b className="num">{savoury}</b> savoury and <b className="num">{sweet}</b> sweet pieces
      per guest.
    </>
  )
}

/** §8.1 Átvétel: delivery or pickup, the zone, the slot and what follows. */
export function Fulfilment({ page }: { page: PageBundle }): JSX.Element {
  const { t, f, state, set, calendar, slots, context, num, param } = page
  const pickup = state.fulfilment === 'pickup'
  const zone = context.zone
  const zoneRow = context.zones[zone]
  /* Specification §3.7: beyond the radius delivery is not offered. Said
     under the question, while delivery is the answer; the customer chooses
     pickup themselves, and the delivery choice stays open so the postcode
     can still be corrected. */
  const beyond = !pickup && outsideDeliveryRadius(context)

  return (
    <div className="card">
      <h3>{t('Átvétel', 'Fulfilment')}</h3>
      <Choices
        id="ful"
        className="opts"
        dataAttribute="ful"
        choices={[
          {
            value: 'delivery',
            label: t('Kiszállítás', 'Delivery'),
            disabled: calendar.ok && calendar.weekend,
          },
          { value: 'pickup', label: t('Személyes átvétel', 'Pickup') },
        ]}
        value={state.fulfilment}
        onChoose={(value) => set({ fulfilment: value as 'delivery' | 'pickup' })}
      />
      {beyond ? (
        <div id="radiusNote">
          <Note tone="stop">{outsideRadiusSentence(t, context)}</Note>
        </div>
      ) : null}
      <div className="grid" style={{ marginTop: 12 }}>
        <div id="zoneWrap" className={pickup ? 'hide' : undefined}>
          <label className="f" htmlFor="zip">
            {t('Irányítószám', 'Postcode')}
          </label>
          <input
            type="text"
            id="zip"
            maxLength={4}
            inputMode="numeric"
            value={state.postcode}
            onChange={(event) =>
              /* DR-22 (FIXES item 25), option C: the step 01 postcode and the
                 address postcode are one value and track each other under
                 pickup as well, so they never diverge. */
              set((current) => ({
                postcode: event.target.value,
                ...syncToAddress(current, event.target.value, page.towns),
              }))
            }
          />
        </div>
        <div id="slotWrap" className={pickup ? 'hide' : undefined}>
          <label className="f" htmlFor="slot">
            {t('Kiszállítási sáv', 'Delivery slot')}
          </label>
          <select
            id="slot"
            disabled={slots.length === 0}
            value={slots.some((slot) => slot.slot_id === state.slotId)
              ? state.slotId
              : (slots[slots.length - 1]?.slot_id ?? '')}
            onChange={(event) => set({ slotId: event.target.value })}
          >
            {slots.length === 0 ? (
              <option value="">{t('nincs sáv', 'no slot')}</option>
            ) : (
              slots.map((slot) => (
                <option key={slot.slot_id} value={slot.slot_id}>
                  {slotWindow(slot)}
                </option>
              ))
            )}
          </select>
        </div>
      </div>

      <div id="fulNote">
        {/* Run 19 §8 (DECIDED 20261016, Barna): a collection on a day other
            than the event date is highlighted, above the note that explains
            the rule — the date, the weekday, the time and how many calendar
            days before the event it is. */}
        <PickupCallout page={page} id="fulPickupAlert" />
        {calendar.ok && calendar.weekend ? (
          <Note>
            {t(
              `Hétvégi eseményre az ételt az előtte lévő munkanapon (${
                calendar.handoverDate === null ? '—' : f.date(calendar.handoverDate, true)
              }) ${param('pickup_to')}-ig lehet átvenni a Graphisoft Parkban. Hétvégén nem szállítunk.`,
              `For a weekend event, collect the food on the business day before (${
                calendar.handoverDate === null ? '—' : f.date(calendar.handoverDate, true)
              }) by ${param('pickup_to')} at Graphisoft Park. We do not deliver at weekends.`,
            )}
          </Note>
        ) : pickup ? (
          <Note tone="ok">
            {t(
              `Átvétel a Graphisoft Parkban az esemény napján, ${
                calendar.ok && calendar.monday ? param('monday_from') : param('pickup_from')
              } és ${param('pickup_to')} között. Minimum ${f.money(num('min_pickup'))}.`,
              `Pickup at Graphisoft Park on the day, between ${
                calendar.ok && calendar.monday ? param('monday_from') : param('pickup_from')
              } and ${param('pickup_to')}. Minimum ${f.money(num('min_pickup'))}.`,
            )}
          </Note>
        ) : slots.length === 0 ? (
          <Note tone="stop">
            {t(
              'Erre a kezdési időpontra nincs kiszállítási sávunk. Válasszon későbbi kezdést vagy személyes átvételt.',
              'There is no delivery slot for this start time. Choose a later start or pickup.',
            )}
          </Note>
        ) : beyond ? null : zoneRow?.fee_quoted === true ? (
          <Note>
            {t(
              'Ez a cím a C zónába esik: a szállítási díjat egyedileg adjuk meg, és a rendelést kollégánk hagyja jóvá.',
              'This address is in zone C: the delivery fee is quoted individually and a colleague approves the order.',
            )}
          </Note>
        ) : (
          <Note tone="ok">
            {t(
              `${zone} zóna · szállítás ${f.money(zoneRow?.fee_gross ?? 0)}, ${f.money(
                zoneRow?.free_above_gross ?? 0,
              )} felett ingyenes · minimum ${f.money(zoneRow?.min_order_gross ?? 0)}`,
              `Zone ${zone} · delivery ${f.money(zoneRow?.fee_gross ?? 0)}, free above ${f.money(
                zoneRow?.free_above_gross ?? 0,
              )} · minimum ${f.money(zoneRow?.min_order_gross ?? 0)}`,
            )}
          </Note>
        )}

        {!pickup && slots.length > 0 ? (
          <p className="sub">
            {t(
              'Csak olyan sávot kínálunk, amellyel az étel még az esemény előtt odaér.',
              'We only offer slots that get the food to you before the event.',
            )}
          </p>
        ) : null}
        {calendar.ok && calendar.monday && !pickup ? (
          <p className="sub">
            {t(
              `Hétfőn ${param('monday_from')} előtt sajnos nem tudunk szállítani.`,
              `On Mondays we do not deliver before ${param('monday_from')}.`,
            )}
          </p>
        ) : null}
        {/* Fix list item 1 (sweep observation 13): on pickup nothing
            arrives — the food is handed over. */}
        <p className="sub">
          {pickup
            ? t(
                'Az ételeket nagy Planteen dobozokban adjuk át, tálalásra készen: tálalhat a dobozokból, vagy átteheti saját tálaira.',
                'We hand the food over in big boxes from The Planteen, ready to set out: serve from the boxes or move it onto your own plates.',
              )
            : t(
                'Az ételek nagy Planteen dobozokban érkeznek, tálalásra készen: tálalhat a dobozokból, vagy átteheti saját tálaira.',
                'The food arrives in big boxes from The Planteen, ready to set out: serve from the boxes or move it onto your own plates.',
              )}
        </p>
      </div>
    </div>
  )
}

/** The end of step 01: the lane and the short-notice check (§8.3). */
function Lane({ page }: { page: PageBundle }): JSX.Element | null {
  const { t, f, calendar, context, priced, num } = page
  if (!calendar.ok) return null
  if (calendar.lane === 'closed') {
    return (
      <div id="laneNote">
        <Note tone="stop">
          {t(
            `Erre a dátumra már nem tudunk rendelést felvenni: legalább ${f.n(num('short_notice_bd'))} munkanap kell az eseményig.`,
            `We can no longer take an order for this date: at least ${num('short_notice_bd')} business days are needed.`,
          )}
        </Note>
      </div>
    )
  }
  if (calendar.lane !== 'short') return null
  const left = context.short_notice.pieces_left
  return (
    <div id="laneNote">
      <Note>
        <b>{t('Rövid határidő – korlátozott választék.', 'Short notice – limited selection.')}</b>{' '}
        {t(
          'Erre a dátumra a gyorsan elkészíthető ételeinkből választhat. A teljes összeget bankkártyával, a rendeléskor kell kifizetni.',
          'For this date you can choose from the dishes we can prepare quickly. The full amount is paid by card when you order.',
        )}
        {left !== null && priced.pieces > left ? (
          <>
            <br />
            {t(
              'Ekkora mennyiséget rövid határidőre csak egyeztetés után tudunk vállalni: ajánlatkérésként küldjük el, és kollégánk felhívja.',
              'We can only take this quantity at short notice after a conversation: it goes as an enquiry and a colleague will call you.',
            )}
          </>
        ) : null}
      </Note>
    </div>
  )
}

/**
 * The chips, in order.
 *
 * Fix list item 6 (Barna, 10 October 2026) led with *Munkaebéd*. **Run 19 §5
 * (DECIDED 20261016, Barna) turns that round**: Ágó's test of 16 October
 * found that the page led with the boxed working lunch although the standing
 * reception is the most common order — so *Állófogadás* is first, and
 * *Könnyű vacsora* sits second, next to the occasion it derives from.
 *
 * The two reception rows share one chip (item B9(a)), so the chip appears
 * where the first of them does; both are named here so the order does not
 * depend on which row the register returns first.
 *
 * Run 18 item R18-3 puts *Egyéb* last. A key this list does not name sorts
 * after the named ones, so `other` is named here rather than left to fall
 * through, which would put it ahead of any later addition that is also
 * unnamed.
 */
export const OCCASION_ORDER: readonly string[] = [
  'fog_nap',
  'fog_est',
  LIGHT_DINNER,
  'munka',
  'konf',
  'coffee',
  OTHER_OCCASION,
]

export function orderedOccasions<T extends { key: string }>(rows: readonly T[]): T[] {
  const rank = (key: string): number => {
    const index = OCCASION_ORDER.indexOf(key)
    return index < 0 ? OCCASION_ORDER.length : index
  }
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => rank(a.row.key) - rank(b.row.key) || a.index - b.index)
    .map(({ row }) => row)
}

/** Opens the browser's own date picker where it has one. `showPicker` throws
 *  without a user gesture or inside a cross-origin frame; then the field
 *  keeps its native behaviour. */
export function openPicker(input: HTMLInputElement): void {
  try {
    input.showPicker?.()
  } catch {
    /* the native control still works */
  }
}

/** The half-hour starts vB6 offers, 07:00 to 22:30. */
const START_TIMES: readonly string[] = Array.from({ length: 32 }, (_, index) => {
  const hour = 7 + Math.floor(index / 2)
  const minute = index % 2 ? '30' : '00'
  return `${String(hour).padStart(2, '0')}:${minute}`
})

/**
 * DR-22 (`build/FIXES.md` item 25): editing the step 01 postcode fills the
 * address block's postcode, and the town where the address has none. The two
 * fields the page calls one value cannot hold two.
 */
function syncToAddress(
  state: import('../state.ts').PageState,
  postcode: string,
  towns: TownTable | null,
): Partial<import('../state.ts').PageState> {
  if (!/^\d{4}$/.test(postcode)) return {}
  const target = state.sameAddress ? 'billing' : 'shipping'
  /* Specification §3.7: the town follows the postcode lookup, and a town the
     customer typed stays. */
  return { [target]: withPostcode(state[target], postcode, towns) }
}
